import React, { useState } from 'react';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  RocketLaunchIcon,
  CheckIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  MegaphoneIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  service: string;
  projectType: string;
  budget: string;
  timeline: string;
  description: string;
  goals: string[];
  currentSituation: string;
}

const QuotePage: React.FC = () => {
  useSEO(pageConfigs.quote);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: '',
    goals: [],
    currentSituation: ''
  });

  const services = [
    { value: 'lead-generation', label: 'Lead Generation', icon: <MegaphoneIcon className="w-5 h-5" /> },
    { value: 'mobile-development', label: 'Mobile Development', icon: <DevicePhoneMobileIcon className="w-5 h-5" /> },
    { value: 'website-development', label: 'Website Development', icon: <ComputerDesktopIcon className="w-5 h-5" /> },
    { value: 'digital-marketing', label: 'Digital Marketing', icon: <ChartBarIcon className="w-5 h-5" /> },
    { value: 'amazon-ads', label: 'Amazon Advertising', icon: <ShoppingBagIcon className="w-5 h-5" /> },
    { value: 'other', label: 'Other / Multiple Services', icon: <UserGroupIcon className="w-5 h-5" /> }
  ];

  const budgetRanges = [
    { value: '5k-10k', label: '$5,000 - $10,000' },
    { value: '10k-25k', label: '$10,000 - $25,000' },
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k+', label: '$100,000+' },
    { value: 'discuss', label: "Let's Discuss" }
  ];

  const timelines = [
    { value: 'asap', label: 'ASAP (Rush Project)' },
    { value: '1-2months', label: '1-2 Months' },
    { value: '3-6months', label: '3-6 Months' },
    { value: '6months+', label: '6+ Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const projectGoals = [
    'Increase Revenue',
    'Generate More Leads',
    'Improve Brand Awareness',
    'Launch New Product/Service',
    'Expand Market Reach',
    'Improve User Experience',
    'Reduce Costs',
    'Automate Processes'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGoalToggle = (goal: string) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert("Thank you! We'll get back to you within 24 hours with a detailed quote.");
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.quote} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom text-center">
          <div className="inline-flex items-center space-x-2 bg-green-100 rounded-full px-4 py-2 mb-6">
            <RocketLaunchIcon className="w-5 h-5 text-green-600" />
            <span className="text-green-700 font-semibold text-sm">Start Your Project</span>
          </div>
          
          <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
            Get Your Free 
            <span className="text-blue-600"> Project Quote</span>
          </h1>
          
          <p className="text-lg text-slate-700 mb-12 max-w-3xl mx-auto leading-relaxed">
            Tell us about your project and we'll provide you with a detailed quote, timeline, 
            and strategic recommendations within 24 hours.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-display font-bold text-slate-900 mb-2">24-Hour Response</h3>
              <p className="text-slate-600">Get your detailed quote and project roadmap within one business day.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CurrencyDollarIcon className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-display font-bold text-slate-900 mb-2">Transparent Pricing</h3>
              <p className="text-slate-600">No hidden fees. Clear breakdown of costs and deliverables.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserGroupIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-display font-bold text-slate-900 mb-2">Expert Consultation</h3>
              <p className="text-slate-600">Strategic recommendations from our experienced team.</p>
            </div>
          </div>

          {/* Quote Form */}
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Contact Information */}
              <div className="bg-slate-50 rounded-2xl p-8">
                <h2 className="text-xl font-display font-bold text-slate-900 mb-6">Contact Information</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your company name"
                    />
                  </div>
                </div>
              </div>

              {/* Service Selection */}
              <div className="bg-slate-50 rounded-2xl p-8">
                <h2 className="text-xl font-display font-bold text-slate-900 mb-6">Service Needed *</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map((service) => (
                    <label key={service.value} className="cursor-pointer">
                      <input
                        type="radio"
                        name="service"
                        value={service.value}
                        checked={formData.service === service.value}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div className={`p-4 rounded-lg border-2 transition-colors ${
                        formData.service === service.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <div className={`${formData.service === service.value ? 'text-blue-600' : 'text-slate-600'}`}>
                            {service.icon}
                          </div>
                          <span className="font-semibold text-slate-900">{service.label}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Project Details */}
              <div className="bg-slate-50 rounded-2xl p-8">
                <h2 className="text-xl font-display font-bold text-slate-900 mb-6">Project Details</h2>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Budget Range *</label>
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select budget range</option>
                        {budgetRanges.map((range) => (
                          <option key={range.value} value={range.value}>{range.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Timeline *</label>
                      <select
                        name="timeline"
                        value={formData.timeline}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select timeline</option>
                        {timelines.map((timeline) => (
                          <option key={timeline.value} value={timeline.value}>{timeline.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Project Description *</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Describe your project, requirements, and any specific features you need..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-4">Project Goals (Select all that apply)</label>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {projectGoals.map((goal) => (
                        <label key={goal} className="cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.goals.includes(goal)}
                            onChange={() => handleGoalToggle(goal)}
                            className="sr-only"
                          />
                          <div className={`p-3 rounded-lg border-2 text-center transition-colors ${
                            formData.goals.includes(goal)
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}>
                            <span className="text-sm font-semibold">{goal}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Current Situation</label>
                    <textarea
                      name="currentSituation"
                      value={formData.currentSituation}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Tell us about your current situation, challenges, or existing solutions..."
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-12 rounded-lg transition-colors flex items-center justify-center space-x-2 mx-auto"
                >
                  <RocketLaunchIcon className="w-5 h-5" />
                  <span>Get My Free Quote</span>
                </button>
                <p className="text-sm text-slate-600 mt-4">
                  We'll respond within 24 hours with your detailed quote and project roadmap.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 mb-4">
              What Happens Next?
            </h2>
            <p className="text-lg text-slate-600">
              Our streamlined process ensures you get the information you need quickly
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="text-lg font-display font-bold text-slate-900 mb-4">Review & Analysis</h3>
              <p className="text-slate-600">Our team reviews your requirements and analyzes the best approach for your project.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="text-lg font-display font-bold text-slate-900 mb-4">Detailed Quote</h3>
              <p className="text-slate-600">Receive a comprehensive quote with timeline, deliverables, and strategic recommendations.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="text-lg font-display font-bold text-slate-900 mb-4">Strategy Call</h3>
              <p className="text-slate-600">Schedule a call to discuss the proposal and refine the project details.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuotePage;